import {
  CLOSE_PATIENT,
  LOAD_ADDRESS_ERROR,
  LOAD_ADDRESS_START,
  LOAD_ADDRESS_SUCCESS,
  LOAD_PATIENT_ERROR,
  LOAD_PATIENT_START,
  LOAD_PATIENT_SUCCESS,
  PatientAction,
  SET_CO_PAYMENT,
  SET_EFACT_EATTEST,
  SET_NOMENCLATURE_CODE,
  SET_NUMBER_OF_PATIENTS,
  SET_PATIENT_TYPE,
  SET_PAYED_AMOUNT,
  SET_PAYMENT_METHOD,
} from "store/types/patient.types";
import * as PatientApi from "api/patient.api";
import { Dispatch } from "redux";
import { Patient } from "model/patient.model";
import { AddressDTO, mapToAddressModel } from "common/dto/Address";
import { HelenaSessionKey } from "model/helena.model";
import { Address } from "common/models/Address";
import { loadHelenaPatient } from "store/actions/helena.actions";
import { HelenaAction } from "store/types/helena.types";
import {
  loadChronicInformation,
  loadInsurability,
} from "store/actions/insurability.actions";
import { InsurabilityAction } from "store/types/insurability.types";
import { PatientTypeEnum } from "common/enums/PatientTypeEnum";
import { EFactEAttestEnum } from "common/enums/EFactEAttestEnum";
import { CoPaymentEnum } from "common/enums/CoPaymentEnum";
import { Nomenclature } from "common/models/Nomenclature";
import { PaymentMethodEnum } from "common/enums/PaymentMethodEnum";
import { i18nPatientContext } from "config/translations/I18n";
import * as PatientAddressApi from "api/patient-address";
import { History } from "history";
import { resetJournal } from "store/actions/journal.actions";
import { JournalAction } from "store/types/journal.types";
import { NotificationAction } from "store/types/notification.types";
import { selectUser } from "store/reducers/user-context.reducer";
import { GlobalState } from "store/configure-store";
import { tarifiedEAttestToday } from "store/actions/tarification.actions";
import { selectTarificationApplicationSession } from "store/reducers/tarification.reducer";
import { TarificationAction } from "store/types/tarification.types";
import { ThunkAction } from "redux-thunk";
import { loadPatientSync } from "store/actions/patient-sync.actions";

/** ---- ACTIONS ---- */
export const closePatient = () => {
  i18nPatientContext.changeLanguage("nl").then(() => {});
  return (dispatch: Dispatch) => {
    dispatch({ type: CLOSE_PATIENT });
  };
};

export const loadPatient = (
  userId: number,
  patientId: number,
  sessionKey: HelenaSessionKey | null,
  history: History
): ThunkAction<
  Promise<void>,
  GlobalState,
  any,
  | PatientAction
  | HelenaAction
  | InsurabilityAction
  | JournalAction
  | NotificationAction
  | TarificationAction
> => {
  return async (dispatch, getState) => {
    const state = getState();
    const tSession = selectTarificationApplicationSession(state);
    try {
      dispatch(dispatchLoadPatientStart());
      const patient = await PatientApi.findPatientById(patientId);
      dispatchLoadPatientSuccess(patient)(dispatch);
      const patientContextLanguage =
        patient.language ?? selectUser(state).language;
      await Promise.all([
        dispatch(fetchAddress(patient)),
        i18nPatientContext.changeLanguage(patientContextLanguage),
        dispatch(loadHelenaPatient(userId, sessionKey, patient)),
        dispatch(loadInsurability(userId, patientId)),
        dispatch(loadPatientSync(userId, patientId)),
        dispatch(loadChronicInformation(userId, patientId)),
        dispatch(resetJournal()),
        dispatch(tarifiedEAttestToday(tSession, userId, patientId)),
      ]);
      history.replace("/consultation");
      return Promise.resolve();
    } catch (e) {
      dispatch(dispatchLoadPatientError(e));
      return Promise.reject();
    }
  };
};

export const fetchAddress = (patient: Patient) => {
  return async (dispatch: Dispatch<PatientAction>) => {
    try {
      dispatchLoadAddressStart()(dispatch);
      const response = await PatientAddressApi.findAddressByPatientId(
        patient.id
      );
      const domicileAddress = getDomicileAddress(response);
      let address = mapToAddressModel(domicileAddress);
      dispatchLoadAddressSuccess(address)(dispatch);
      patient.address = address;
    } catch (e) {
      dispatchLoadAddressError()(dispatch);
    }
  };
};

const getDomicileAddress = (
  addresses: AddressDTO[]
): AddressDTO | undefined => {
  return addresses && addresses.find((a) => a.addressType === "DOMICILE");
};

/** ---- DISPATCHERS ---- */
const dispatchLoadPatientStart = () => {
  return (dispatch: Dispatch<PatientAction>) => {
    dispatch({
      type: LOAD_PATIENT_START,
    });
  };
};

const dispatchLoadPatientSuccess = (response: Patient) => {
  return (dispatch: Dispatch<PatientAction>) => {
    dispatch({
      type: LOAD_PATIENT_SUCCESS,
      payload: response,
    });
  };
};

export const setPayedAmount = (payedAmount: number) => {
  return (dispatch: Dispatch<PatientAction>) => {
    return dispatch({ type: SET_PAYED_AMOUNT, payload: payedAmount });
  };
};

const dispatchLoadPatientError = (e: any) => {
  return (dispatch: Dispatch<PatientAction>) => {
    dispatch({
      type: LOAD_PATIENT_ERROR,
      payload: e,
    });
  };
};

const dispatchLoadAddressStart = () => {
  return (dispatch: Dispatch<PatientAction>) => {
    dispatch({
      type: LOAD_ADDRESS_START,
    });
  };
};

const dispatchLoadAddressSuccess = (response: Address) => {
  return (dispatch: Dispatch<PatientAction>) => {
    dispatch({
      type: LOAD_ADDRESS_SUCCESS,
      payload: response,
    });
  };
};

const dispatchLoadAddressError = () => {
  return (dispatch: Dispatch<PatientAction>) => {
    dispatch({
      type: LOAD_ADDRESS_ERROR,
    });
  };
};

export const setPatientType = (patientType: PatientTypeEnum) => {
  return (dispatch: Dispatch<PatientAction>) => {
    return dispatch({ type: SET_PATIENT_TYPE, payload: patientType });
  };
};

export const setEFactEAttest = (eFacteAttest: EFactEAttestEnum) => {
  return (dispatch: Dispatch<PatientAction>) => {
    return dispatch({ type: SET_EFACT_EATTEST, payload: eFacteAttest });
  };
};

export const setCoPayment = (coPayment: CoPaymentEnum) => {
  return (dispatch: Dispatch<PatientAction>) => {
    return dispatch({ type: SET_CO_PAYMENT, payload: coPayment });
  };
};

export const setNomenclatureCode = (nomenclatureCode: Nomenclature[]) => {
  return (dispatch: Dispatch<PatientAction>) => {
    return dispatch({ type: SET_NOMENCLATURE_CODE, payload: nomenclatureCode });
  };
};

export const setNumberOfPatients = (numberOfPatients: number) => {
  return (dispatch: Dispatch<PatientAction>) => {
    return dispatch({
      type: SET_NUMBER_OF_PATIENTS,
      payload: numberOfPatients,
    });
  };
};

export const setPaymentMethod = (paymentMethod: PaymentMethodEnum) => {
  return (dispatch: Dispatch<PatientAction>) => {
    return dispatch({ type: SET_PAYMENT_METHOD, payload: paymentMethod });
  };
};
